import { COMPONENT_PLACEMENT_FRONT_ONLY } from '../../../component-classes/constants';
import { getDefaultPlacementCenterYCfn } from '../../panel-items/functions/get-default-placement-center-y.cfn';
import { nearestSixteenth } from '../../../helpers';
import { PanelItemGroup } from '../panel-item-group.class';

/**
 * automagically distributes items vertically based on overall size
 * @param panelItemGroup PanelItemGroup containing items to sort
 * @returns void
 */
export const centerItemsVerticallyCfn = (panelItemGroup: PanelItemGroup): void => {
	// console.log('center-items-vertically-cfn', panelItemGroup);
	// short circuit if nothing was found or appliance group has no appliances
	if (!panelItemGroup || panelItemGroup.items.children.length === 0) return;

	const panel = panelItemGroup.refMap.panel ?? null;
	const island = panelItemGroup.refMap.island ?? null;

	// position bottom-bound item at panel bottom
	const bottomPanelItem = panelItemGroup.getBottomBoundItem();
	if (bottomPanelItem && panel) {
		const halfPanelHeight = panel.size.height / 2;
		bottomPanelItem.center.y = halfPanelHeight - (bottomPanelItem.item.size.height / 2);
	}

	// get items that are front only
	// Note: items are sorted by offset.y
	const frontItems = panelItemGroup.items.filterItems(false, [COMPONENT_PLACEMENT_FRONT_ONLY]);

	// return if there are no front items
	if (frontItems === null || frontItems.length === 0) return;

	// get top and bottom bound appliance items if any
	const topPanelItem = panelItemGroup.getTopBoundItem();
	let distanceBelowCountertop = topPanelItem?.item.distanceBelowCountertop ?? 0;
	const thickness = island.getCountertop().material.thickness ?? 0;

	if (distanceBelowCountertop > 0) distanceBelowCountertop -= thickness;

	// for front-only bound items (e.g. vents), use their full height as reserved space
	const topReservedHeight = topPanelItem
		? (topPanelItem.item.placement === COMPONENT_PLACEMENT_FRONT_ONLY
			? topPanelItem.item.size.height
			: distanceBelowCountertop)
		: 0;

	// calculate remaining space after top and bottom sizes are removed
	const netPanelHeight = panel.size.height -
		topReservedHeight -
		(bottomPanelItem?.item.size.height ?? 0);

	// exclude bound items from distribution — they stay at their edge positions
	const itemsToDistribute = frontItems.filter((pi) => {
		if (topPanelItem && pi.id === topPanelItem.id) return false;
		if (bottomPanelItem && pi.id === bottomPanelItem.id) return false;
		// a catalog defaultPlacement is a declared position, so it holds like a bound item rather than being evenly spaced
		if (getDefaultPlacementCenterYCfn(pi.item, panel.size.height) !== null) return false;
		return true;
	});

	// calculate equal spacing
	const ttlItemHeight = itemsToDistribute.reduce((acc, pi) => acc + pi.item.size.height, 0);
	const numAppliances = itemsToDistribute.length;
	const spacing = (netPanelHeight - ttlItemHeight) / (numAppliances + 1);

	// set prevTop starting position
	let prevTop = bottomPanelItem
		? bottomPanelItem.center.y + (bottomPanelItem.item.size.height / 2)
		: panel.size.height / 2

	// loop through distributable items, setting y offset
	itemsToDistribute.forEach((pi) => {
		// console.log('centerItemsVertically', { i, sku: pi.item.fqSku, prevTop, spacing, topPanelItem, bottomPanelItem, netPanelHeight })
		const halfHeight = pi.item.size.height / 2;
		pi.center.y = nearestSixteenth(prevTop - spacing - halfHeight);
		prevTop = pi.center.y - halfHeight;
	})

	// console.log('cenerItemsVertically', { topPanelItem, bottomPanelItem, frontItems, netPanelHeight, numAppliances, spacing });
};
